/** Room rules and rule sets. See scope.md §4. Validated server-side; never trusted from a client. */

export type PackRule = 'auto' | 'two' | 'three';
export type AceRule = 'low' | 'high' | 'both';
export type LayDownRule = 'exact' | 'atLeast';
export type LayOffRule = 'sameTurn' | 'turnAfter' | 'never';
export type JokerSwapRule = 'any' | 'othersOnly' | 'off';
export type ClaimPenalty = 'oneCard' | 'none';
export type ClaimsPerRound = 'unlimited' | 1 | 2 | 3;
export type GoOutBonus = 'finalRound' | 'everyRound' | 'none';
export type ScoringTable = 'standard' | 'harsh';
export type FinalContract =
  /** Three runs using every card in hand, so there is no final discard. The house rule. */
  | 'threeRunsAllCards'
  /** Three runs of exactly four, then a discard like every other round. */
  | 'threeRunsOf4'
  | 'twoRunsOf6'
  /** One run of the whole suit. Measured as effectively unachievable — see scope.md §3.3. */
  | 'run13';

export type RoomRules = {
  packs: PackRule;
  ace: AceRule;
  finalContract: FinalContract;
  layDown: LayDownRule;
  layOff: LayOffRule;
  layOffOnOthers: boolean;
  jokerSwap: JokerSwapRule;
  claimWindowMs: number;
  claimPenalty: ClaimPenalty;
  claimsPerRound: ClaimsPerRound;
  goOutBonus: GoOutBonus;
  scoring: ScoringTable;
  turnTimerMs: number | null;
};

/** Exactly §3 — the way it is played at home. */
export const DEFAULT_RULES: RoomRules = {
  packs: 'auto',
  ace: 'both',
  finalContract: 'threeRunsAllCards',
  /** A run may be longer than the contract asks. Ben's call, 14 September 2026. §3.3. */
  layDown: 'atLeast',
  layOff: 'sameTurn',
  layOffOnOthers: true,
  jokerSwap: 'any',
  claimWindowMs: 5000,
  claimPenalty: 'oneCard',
  claimsPerRound: 'unlimited',
  goOutBonus: 'finalRound',
  scoring: 'standard',
  turnTimerMs: null,
};

export type RuleSet = { id: string; name: string; description: string; rules: RoomRules };

/** The rule sets that ship with the game. scope.md §4.2. */
export const BUILT_IN_RULE_SETS: RuleSet[] = [
  {
    id: 'house',
    name: 'House rules',
    description: 'The way it is played at home.',
    rules: DEFAULT_RULES,
  },
  {
    id: 'traditional',
    name: 'Traditional',
    description: 'Ace high or low, three runs of four to finish, three asks a round.',
    rules: {
      ...DEFAULT_RULES,
      finalContract: 'threeRunsOf4',
      layDown: 'exact',
      claimsPerRound: 3,
    },
  },
  {
    id: 'gentle',
    name: 'Gentle',
    description: 'Kinder for younger players: fifteen seconds to ask, and asking is free.',
    rules: {
      ...DEFAULT_RULES,
      claimWindowMs: 15000,
      claimPenalty: 'none',
      finalContract: 'twoRunsOf6',
    },
  },
];

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;
export const MIN_CLAIM_WINDOW_MS = 1000;
export const ALLOWED_CLAIM_WINDOWS_MS = [1000, 3000, 5000, 10000, 15000] as const;

/** Packs in play. scope.md §3.1. */
export function packsForPlayers(playerCount: number, rule: PackRule): number {
  if (rule === 'two') return 2;
  if (rule === 'three') return 3;
  return playerCount <= 4 ? 2 : 3;
}

export const SCORE_TABLES = {
  standard: { low: 5, ten: 10, ace: 15, joker: 20 },
  harsh: { low: 5, ten: 10, ace: 20, joker: 50 },
} as const;

export const GO_OUT_BONUS = -50;

/**
 * The time to ask for a card uncovered by an out-of-turn take: about half the main window,
 * in whole seconds, never under one. Older cards are wanted less often, so the table should
 * not wait as long for them. Ben, 1 October 2026. §3.5.
 */
export function uncoveredWindowMs(claimWindowMs: number): number {
  return Math.max(1000, Math.ceil(claimWindowMs / 2000) * 1000);
}

/** The most cards one draw may take from the discard pile. Ben's call, 1 October 2026. §3.4. */
export const MAX_DISCARD_TAKE = 10;

/**
 * Laps of the table, with the stock empty, the pile too thin to rebuild it and nothing
 * put on the table, before the players are offered the chance to end the round. §3.8.
 */
export const DEAD_PILE_LAPS = 2;

/** Packs that may be shuffled into one stalled round. "An extra deck" — Ben, 1 October 2026. */
export const MAX_EXTRA_PACKS = 1;
export const ROUNDS = 7;

/** A run is never shorter than this. scope.md §3.2. */
export const MIN_RUN = 4;
